import React, { useState } from "react";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, formatApiError } from "../../lib/api";
import HabitLoopDiagram from "../habits/HabitLoopDiagram";
import { addIfThenBlock } from "../../lib/habitLoopPdfExtras";

/**
 * Public landing-page lead magnet — visitor sees the printable Habit
 * Loop diagram on the left, an email-capture card on the right.
 *
 * Submitting the form:
 *   1. POSTs the lead to /api/habit-loop/claim-pdf so it lands in the
 *      same `leads` collection as quiz email-gate leads.
 *   2. Triggers a client-side PDF download (same path /app/habits uses).
 *   3. Backend sends the "your printable Habit Loop is here" email
 *      with a CTA inviting them to take the MoneyMind Quiz next —
 *      that's the funnel into the persona nurture sequence.
 */
export default function HabitLoopLeadMagnet() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscribeBlog, setSubscribeBlog] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const triggerLocalPdfDownload = async () => {
    // Reuse the same SVG-to-PDF pipeline as the in-app component but
    // build the diagram off-screen so the landing page doesn't render
    // a duplicate SVG just for download.
    try {
      const { jsPDF } = await import("jspdf");
      // Build the SVG markup via a temporary container.
      const ReactDOMServer = await import("react-dom/server");
      const svgString = ReactDOMServer.renderToStaticMarkup(
        <HabitLoopDiagram />,
      );
      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      const scale = 3;
      const size = 600 * scale;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, size, size);
          URL.revokeObjectURL(url);
          resolve();
        };
        img.onerror = (e) => {
          URL.revokeObjectURL(url);
          reject(e);
        };
        img.src = url;
      });
      const pngDataUrl = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "in",
        format: "letter",
      });
      const imgSize = 6.5;
      const x = (8.5 - imgSize) / 2;
      pdf.addImage(pngDataUrl, "PNG", x, 1.0, imgSize, imgSize);
      addIfThenBlock(pdf);
      pdf.setFontSize(9);
      pdf.setTextColor(120, 118, 113);
      pdf.text(
        "Print, pin it, build the loop. — financialhabitstosucceed.com",
        4.25, 10.5, { align: "center" },
      );
      pdf.save("finhabits-habit-loop.pdf");
    } catch (e) {
      // PDF generation failure isn't fatal — the user still receives
      // the lead-capture email from the backend with the same content.
      console.error("Landing Habit Loop PDF download failed:", e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Pull a marketing ref from the URL if present (?ref=...) so the
      // funnel report can split-test placements of this lead magnet.
      const ref = (() => {
        try {
          return new URLSearchParams(window.location.search).get("ref");
        } catch {
          return null;
        }
      })();
      await api.post("/habit-loop/claim-pdf", {
        email: email.trim(),
        name: name.trim() || null,
        subscribe_blog: subscribeBlog,
        ref: ref || null,
      });
      // Fire and forget — never block the success UI on local PDF gen.
      triggerLocalPdfDownload();
      // Meta Pixel Lead event so this surface earns its own attribution
      // signal independent of the quiz funnel.
      try {
        const { trackLead } = await import("../../lib/metaPixel");
        trackLead({
          content_name: "Habit Loop Printable",
          content_category: "lead-magnet",
        });
      } catch {
        // pixel unavailable — no-op
      }
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24"
      data-testid="habit-loop-lead-magnet"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Diagram preview — left */}
        <div className="lg:col-span-6">
          <div className="bg-[#F7F5F0] border border-[#E6E3DB] p-4 md:p-6 shadow-sm">
            <HabitLoopDiagram />
          </div>
        </div>

        {/* Email-gate card — right */}
        <div className="lg:col-span-6">
          <p className="eyebrow text-[#C05C3D] mb-3" data-testid="leadmagnet-eyebrow">
            Free printable
          </p>
          <h2 className="text-4xl sm:text-5xl font-serif font-light leading-tight">
            Pin the loop where you check your money.
          </h2>
          <p className="mt-5 text-lg text-[#4d4f4c] leading-relaxed max-w-xl">
            One page, on-brand, ready to print. Cue · Routine · Reward,
            with your Vision at the center. Drop your email and we&apos;ll
            send the PDF — and a starter loop matched to how you
            actually think about money.
          </p>

          {done ? (
            <div
              className="mt-8 p-5 bg-[#E8EDE3] border border-[#3E6B37] rounded-md flex items-start gap-3"
              data-testid="leadmagnet-success"
            >
              <CheckCircle2 className="h-5 w-5 text-[#3E6B37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-[#2C302E]">
                  Your Habit Loop is on the way.
                </p>
                <p className="text-sm text-[#4d4f4c] mt-1 leading-relaxed">
                  Check {email}. We&apos;ve also kicked off the PDF download in this tab. Next step → take the 5-min MoneyMind Quiz so we can match a starter loop to you.
                </p>
                <a
                  href="/quiz?ref=habit-loop-leadmagnet"
                  className="inline-flex items-center gap-2 mt-3 text-sm font-medium text-[#3E6B37] hover:text-[#2e5428]"
                  data-testid="leadmagnet-quiz-cta"
                >
                  Take the MoneyMind&trade; Quiz
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="mt-8 max-w-md"
              data-testid="leadmagnet-form"
            >
              <label className="block text-sm font-medium text-[#2C302E] mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 border border-[#E6E3DB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#3E6B37] focus:border-transparent"
                data-testid="leadmagnet-email"
              />
              <label className="block text-sm font-medium text-[#2C302E] mt-3 mb-1">
                First name <span className="text-[#9b9892] font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full px-3 py-2.5 border border-[#E6E3DB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#3E6B37] focus:border-transparent"
                data-testid="leadmagnet-name"
              />
              <div className="mt-4 flex items-start gap-3 p-3 bg-[#F1EEE5] rounded-md border border-[#E6E3DB]">
                <Switch
                  checked={subscribeBlog}
                  onCheckedChange={setSubscribeBlog}
                  data-testid="leadmagnet-blog-toggle"
                  aria-label="Subscribe to the weekly Finhabits blog post"
                  className="mt-0.5"
                />
                <label className="text-sm text-[#2C302E] leading-relaxed cursor-pointer flex-1">
                  <span className="font-medium">Send me the weekly Finhabits blog post.</span>
                  <span className="block text-xs text-[#787671] mt-0.5">
                    One short read per week. Unsubscribe anytime.
                  </span>
                </label>
              </div>
              {error && (
                <p
                  className="mt-3 p-2.5 text-sm bg-[#FFF6F1] border border-[#C05C3D] text-[#C05C3D] rounded"
                  data-testid="leadmagnet-error"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-sm bg-[#C05C3D] text-white hover:bg-[#A34A2E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="leadmagnet-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Email me the Habit Loop
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
              <p className="mt-3 text-[11px] text-[#9b9892] leading-relaxed">
                We&apos;ll send the PDF immediately. We never share or sell your email.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
